interface FlowEdgeReference {
  targetId: string;
  via?: string;
}

export interface FlowNodeSummary {
  id: string;
  label: string;
  title?: string;
  type?: string;
  outgoing: FlowEdgeReference[];
  incoming: FlowEdgeReference[];
}

export interface FlowEdgeSummary {
  sourceId: string;
  targetId: string;
  via?: string;
}

export interface FlowStructureAnalysis {
  nodes: FlowNodeSummary[];
  edges: FlowEdgeSummary[];
  nodeCount: number;
  laneCount?: number;
  warnings: string[];
  nodeLookup: Map<string, FlowNodeSummary>;
}

export interface FlowExtractionSummary {
  keptNodeIds: string[];
  removedNodeIds: string[];
  edgeCount: number;
  nodeCount: number;
}

export interface FlowExtractionResult {
  json: unknown;
  summary: FlowExtractionSummary;
}

export interface SegmentSelectionSuccess {
  success: true;
  nodeIds: string[];
  edgeCount: number;
}

export interface SegmentSelectionFailure {
  success: false;
  error: string;
}

export type SegmentSelectionResult = SegmentSelectionSuccess | SegmentSelectionFailure;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getNodesContainer(source: unknown): Record<string, unknown> | null {
  if (!isPlainObject(source)) {
    return null;
  }

  if (isPlainObject(source.content) && isPlainObject(source.content.nodes)) {
    return source.content.nodes as Record<string, unknown>;
  }

  if (isPlainObject((source as Record<string, unknown>).nodes)) {
    return (source as Record<string, unknown>).nodes as Record<string, unknown>;
  }

  return null;
}

function getCommentContainer(
  source: unknown
): { comments?: unknown[] } | null {
  if (!isPlainObject(source)) {
    return null;
  }
  if (isPlainObject(source.content)) {
    return source.content as { comments?: unknown[] };
  }
  return source as { comments?: unknown[] };
}

function normalizeNodeId(value: unknown): string | null {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

export function analyzeFlowStructure(
  source: unknown
): FlowStructureAnalysis | null {
  if (!isPlainObject(source)) {
    return null;
  }

  const nodesContainer = getNodesContainer(source);
  if (!nodesContainer) {
    return null;
  }

  const nodeLookup = new Map<string, FlowNodeSummary>();
  const edges: FlowEdgeSummary[] = [];
  const warnings: string[] = [];

  Object.entries(nodesContainer).forEach(([rawId, rawNodeValue]) => {
    const rawNode = rawNodeValue;
    if (!isPlainObject(rawNode)) {
      return;
    }

    const id = normalizeNodeId(rawId);
    if (!id) {
      return;
    }

    const nodeObject = rawNode as Record<string, unknown>;
    const meta = isPlainObject(nodeObject.meta) ? nodeObject.meta : {};
    const data = isPlainObject(nodeObject.data) ? nodeObject.data : {};

    const labelCandidates = [
      typeof meta.title === 'string' ? meta.title : null,
      typeof meta.label === 'string' ? meta.label : null,
      typeof nodeObject.name === 'string' ? nodeObject.name : null,
      typeof nodeObject.label === 'string'
        ? (nodeObject.label as string)
        : null
    ].filter((candidate): candidate is string => Boolean(candidate));

    const label =
      labelCandidates.find((candidate) => candidate.trim().length > 0) ??
      `Node ${id}`;

    const summary: FlowNodeSummary = {
      id,
      label,
      title:
        typeof meta.title === 'string' && meta.title.trim().length > 0
          ? meta.title
          : undefined,
      type:
        typeof data.type === 'string' && data.type.trim().length > 0
          ? data.type
          : undefined,
      outgoing: [],
      incoming: []
    };

    nodeLookup.set(id, summary);

    const outputs = isPlainObject(nodeObject.outputs) ? nodeObject.outputs : {};
    Object.entries(outputs).forEach(([portName, rawPort]) => {
      if (!isPlainObject(rawPort)) {
        return;
      }
      const connections = Array.isArray(rawPort.connections)
        ? rawPort.connections
        : [];
      connections.forEach((connection) => {
        if (!isPlainObject(connection)) {
          return;
        }
        const targetId = normalizeNodeId(connection.node);
        if (!targetId) {
          return;
        }
        edges.push({
          sourceId: id,
          targetId,
          via:
            typeof portName === 'string' && portName.trim().length > 0
              ? portName
              : undefined
        });
      });
    });
  });

  edges.forEach((edge) => {
    const sourceNode = nodeLookup.get(edge.sourceId);
    if (sourceNode) {
      sourceNode.outgoing.push({
        targetId: edge.targetId,
        via: edge.via
      });
    }
    const targetNode = nodeLookup.get(edge.targetId);
    if (targetNode) {
      targetNode.incoming.push({
        targetId: edge.sourceId,
        via: edge.via
      });
    }
  });

  if (nodeLookup.size === 0) {
    warnings.push('No nodes detected in the provided JSON.');
  }

  const laneCountCandidate = isPlainObject(source.content)
    ? source.content.lanes
    : (source as Record<string, unknown>).lanes;

  const laneCount = Array.isArray(laneCountCandidate)
    ? laneCountCandidate.length
    : undefined;

  const nodes = Array.from(nodeLookup.values()).sort((a, b) => {
    const first = a.label.toLowerCase();
    const second = b.label.toLowerCase();
    if (first === second) {
      return a.id.localeCompare(b.id);
    }
    return first.localeCompare(second);
  });

  return {
    nodes,
    edges,
    nodeCount: nodes.length,
    laneCount,
    warnings,
    nodeLookup
  };
}

export function extractFlowSubset(
  source: unknown,
  nodeIds: Iterable<string>
): FlowExtractionResult | null {
  if (!isPlainObject(source)) {
    return null;
  }

  const nodesContainer = getNodesContainer(source);
  if (!nodesContainer) {
    return null;
  }

  const selection = new Set(
    Array.from(nodeIds)
      .map((id) => normalizeNodeId(id))
      .filter((id): id is string => Boolean(id))
  );

  if (selection.size === 0) {
    return null;
  }

  let cloned;
  try {
    cloned = JSON.parse(JSON.stringify(source));
  } catch (error) {
    return null;
  }

  const clonedNodesContainer = getNodesContainer(cloned);
  if (!clonedNodesContainer) {
    return null;
  }

  const preservedEntries = Object.entries(clonedNodesContainer).filter(
    ([rawId]) => selection.has(normalizeNodeId(rawId) || '')
  );

  const preservedNodes: Record<string, unknown> = {};
  preservedEntries.forEach(([rawId, rawNode]) => {
    const id = normalizeNodeId(rawId);
    if (!id || !isPlainObject(rawNode)) {
      return;
    }

    preservedNodes[rawId] = rawNode;

    const nodeObject = rawNode as Record<string, any>;

    const inputs = isPlainObject(nodeObject.inputs) ? nodeObject.inputs : {};
    Object.entries(inputs).forEach(([inputName, inputValue]) => {
      if (!isPlainObject(inputValue)) {
        return;
      }
      if (!Array.isArray(inputValue.connections)) {
        inputValue.connections = [];
        return;
      }
      inputValue.connections = inputValue.connections.filter((connection) => {
        if (!isPlainObject(connection)) {
          return false;
        }
        const connectedId = normalizeNodeId(connection.node);
        return connectedId ? selection.has(connectedId) : false;
      });
      if (
        Array.isArray(inputValue.connections) &&
        inputValue.connections.length === 0
      ) {
        inputValue.connections = [];
      }
      nodeObject.inputs[inputName] = inputValue;
    });

    const outputs = isPlainObject(nodeObject.outputs) ? nodeObject.outputs : {};
    Object.entries(outputs).forEach(([outputName, outputValue]) => {
      if (!isPlainObject(outputValue)) {
        return;
      }
      const connections = Array.isArray(outputValue.connections)
        ? outputValue.connections
        : [];
      outputValue.connections = connections.filter((connection) => {
        if (!isPlainObject(connection)) {
          return false;
        }
        const connectedId = normalizeNodeId(connection.node);
        return connectedId ? selection.has(connectedId) : false;
      });
      nodeObject.outputs[outputName] = outputValue;
    });
  });

  if (isPlainObject(cloned.content) && isPlainObject(cloned.content.nodes)) {
    cloned.content.nodes = preservedNodes;
  } else {
    (cloned as Record<string, unknown>).nodes = preservedNodes;
  }

  const commentContainer = getCommentContainer(cloned);
  if (commentContainer && Array.isArray(commentContainer.comments)) {
    commentContainer.comments = commentContainer.comments
      .map((comment) => {
        if (!isPlainObject(comment)) {
          return comment;
        }
        if (Array.isArray(comment.links)) {
          comment.links = comment.links.filter((link) => {
            const normalized = normalizeNodeId(link);
            return normalized ? selection.has(normalized) : false;
          });
        }
        return comment;
      })
      .filter((comment) => {
        if (!isPlainObject(comment)) {
          return true;
        }
        if (!Array.isArray(comment.links)) {
          return true;
        }
        return comment.links.length > 0;
      });
  }

  const analysis = analyzeFlowStructure(cloned);

  const removedNodeIds = Object.keys(nodesContainer)
    .map((id) => normalizeNodeId(id))
    .filter((id): id is string => Boolean(id))
    .filter((id) => !selection.has(id));

  return {
    json: cloned,
    summary: {
      keptNodeIds: Array.from(selection),
      removedNodeIds,
      edgeCount: analysis ? analysis.edges.length : 0,
      nodeCount: analysis ? analysis.nodeCount : preservedEntries.length
    }
  };
}

export function selectNodesSegment(
  analysis: FlowStructureAnalysis,
  rawStartId: string,
  rawEndId: string
): SegmentSelectionResult {
  const startId = normalizeNodeId(rawStartId);
  const endId = normalizeNodeId(rawEndId);

  if (!startId || !endId) {
    return { success: false, error: 'Start and end node IDs are required.' };
  }

  if (!analysis.nodeLookup.has(startId)) {
    return { success: false, error: `Node ${startId} was not found in the JSON.` };
  }

  if (!analysis.nodeLookup.has(endId)) {
    return { success: false, error: `Node ${endId} was not found in the JSON.` };
  }

  const outgoing = new Map<string, string[]>();
  const incoming = new Map<string, string[]>();

  analysis.nodes.forEach((node) => {
    outgoing.set(node.id, []);
    incoming.set(node.id, []);
  });

  analysis.edges.forEach((edge) => {
    if (outgoing.has(edge.sourceId)) {
      outgoing.get(edge.sourceId)!.push(edge.targetId);
    }
    if (incoming.has(edge.targetId)) {
      incoming.get(edge.targetId)!.push(edge.sourceId);
    }
  });

  const reachableFromStart = new Set<string>();
  const forwardQueue: string[] = [startId];
  while (forwardQueue.length > 0) {
    const current = forwardQueue.shift();
    if (!current || reachableFromStart.has(current)) {
      continue;
    }
    reachableFromStart.add(current);
    const neighbours = outgoing.get(current);
    if (neighbours) {
      neighbours.forEach((neighbour) => {
        if (!reachableFromStart.has(neighbour)) {
          forwardQueue.push(neighbour);
        }
      });
    }
  }

  if (!reachableFromStart.has(endId)) {
    return {
      success: false,
      error: `Node ${endId} is not reachable from node ${startId}.`
    };
  }

  const canReachEnd = new Set<string>();
  const backwardQueue: string[] = [endId];
  while (backwardQueue.length > 0) {
    const current = backwardQueue.shift();
    if (!current || canReachEnd.has(current)) {
      continue;
    }
    canReachEnd.add(current);
    const neighbours = incoming.get(current);
    if (neighbours) {
      neighbours.forEach((neighbour) => {
        if (!canReachEnd.has(neighbour)) {
          backwardQueue.push(neighbour);
        }
      });
    }
  }

  const selectedNodeIds: string[] = [];
  reachableFromStart.forEach((id) => {
    if (canReachEnd.has(id)) {
      selectedNodeIds.push(id);
    }
  });

  if (!selectedNodeIds.includes(startId) || !selectedNodeIds.includes(endId)) {
    return {
      success: false,
      error: `Unable to build a connected path between ${startId} and ${endId}.`
    };
  }

  selectedNodeIds.sort((a, b) => {
    const indexA = analysis.nodes.findIndex((node) => node.id === a);
    const indexB = analysis.nodes.findIndex((node) => node.id === b);
    return indexA - indexB;
  });

  const edgeCount = analysis.edges.filter(
    (edge) => selectedNodeIds.includes(edge.sourceId) && selectedNodeIds.includes(edge.targetId)
  ).length;

  return {
    success: true,
    nodeIds: selectedNodeIds,
    edgeCount
  };
}
